/**
 * Web Storage access that never throws. In a document denied storage (cookies blocked, sandboxed frame),
 * merely reading `window.localStorage` raises a SecurityError, so `typeof localStorage` guards do not protect.
 * Values that cannot be stored stay in memory, so a session still works until the page is reloaded.
 */
const createSafeStorage = (getStorage: () => Storage) => {
  const memory = new Map<string, string>();

  return {
    getItem(key: string): string | null {
      const pending = memory.get(key);
      if (pending !== undefined) return pending;
      try {
        return getStorage().getItem(key);
      } catch {
        return null;
      }
    },

    /** Returns true only when the value really reached the browser storage and will survive a reload. */
    setItem(key: string, value: string): boolean {
      try {
        getStorage().setItem(key, value);
        memory.delete(key);
        return true;
      } catch {
        memory.set(key, value);
        return false;
      }
    },

    removeItem(key: string): void {
      memory.delete(key);
      try {
        getStorage().removeItem(key);
      } catch { /* storage unavailable */ }
    },
  };
};

export const safeLocalStorage = createSafeStorage(() => localStorage);
export const safeSessionStorage = createSafeStorage(() => sessionStorage);
